import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, Check, Lock, ShieldCheck, Users } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

/**
 * Plan-aware sign-up page.
 *
 * The landing page sends people here with the plan and billing cycle they
 * clicked. This page shows exactly what they will pay — seats, per-seat rate
 * and the total for the cycle — and then hands them to Microsoft's hosted
 * create-account form (name, email, password) in one click. They never see an
 * account picker, because a first-time visitor has no account to pick.
 *
 * The choice is stored locally as an intent so the Billing page can pre-fill
 * the same plan and cycle after the account exists. Nothing is charged until
 * the firm confirms the invoice.
 */

const PLAN_COPY = {
  screening: {
    name: 'Screening',
    seats: 2,
    yearly: 250,
    monthly: 300,
    line: 'Triage far more deals than you can read.',
    includes: ['15 companies a month', 'CIM and email intake', 'Thesis scoring with named gaps', 'Financial standardization'],
  },
  deal_team: {
    name: 'Deal Team',
    seats: 3,
    yearly: 500,
    monthly: 600,
    line: 'The full deal lifecycle, from scan to signed IOI.',
    includes: ['Everything in Screening, unlimited deals', 'Deal Review, request lists, workstreams', 'IOI, LOI, IC memo, term sheet', 'LBO and operating model'],
  },
  firm: {
    name: 'Firm',
    seats: 8,
    yearly: 850,
    monthly: 1020,
    line: 'The portfolio on the same screen as the pipeline.',
    includes: ['Everything in Deal Team', 'Monitoring, decline alerts, board decks', 'Multi-fund workspaces', 'SSO, audit log, role controls'],
  },
};

export const SIGNUP_INTENT_KEY = 'caply_signup_intent';

export default function Signup() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { signup, token } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const planId = PLAN_COPY[params.get('plan')] ? params.get('plan') : 'deal_team';
  const cycle = params.get('cycle') === 'monthly' ? 'monthly' : 'annual';
  const plan = PLAN_COPY[planId];

  const seatCount = Math.max(plan.seats, Number(params.get('seats')) || plan.seats);
  const rate = cycle === 'monthly' ? plan.monthly : plan.yearly;
  const monthlyEquivalent = rate * seatCount;
  const invoiceTotal = cycle === 'monthly' ? monthlyEquivalent : monthlyEquivalent * 12;

  const money = (n) => `$${n.toLocaleString('en-US')}`;

  const summary = useMemo(() => ({
    plan: planId,
    cycle,
    seats: seatCount,
    per_seat: rate,
    invoice_total: invoiceTotal,
  }), [planId, cycle, seatCount, rate, invoiceTotal]);

  const start = async () => {
    setBusy(true);
    setError('');
    try {
      localStorage.setItem(SIGNUP_INTENT_KEY, JSON.stringify(summary));
      await signup();
    } catch (err) {
      setBusy(false);
      setError('Could not open the Caply sign-up page. Try again, or sign in if you already have an account.');
    }
  };

  if (token) {
    return (
      <div className="mx-auto max-w-lg px-5 py-24 text-center">
        <h1 className="text-2xl font-semibold text-navy-900">You are already signed in</h1>
        <p className="mt-2 text-sm text-slate-600">
          Head to the workspace, or open Plan &amp; Billing to activate {plan.name}.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <button onClick={() => navigate('/portfolio')} className="rounded-md bg-navy-900 px-5 py-2.5 text-sm font-semibold text-brand-100">
            Go to My Companies
          </button>
          <button onClick={() => navigate('/billing')} className="rounded-md border border-slate-300 px-5 py-2.5 text-sm font-semibold text-navy-900">
            Plan &amp; Billing
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="mx-auto flex max-w-5xl items-center justify-between px-5 py-5">
        <Link to="/" className="text-sm font-semibold tracking-tight text-navy-900">Caply</Link>
        <Link to="/login" className="text-[13px] font-medium text-slate-600 hover:text-navy-900">Sign in</Link>
      </header>

      <main className="mx-auto grid max-w-5xl gap-6 px-5 pb-16 lg:grid-cols-[1.1fr_0.9fr]">
        <section className="rounded-2xl border border-slate-200 bg-white p-7">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-brand-700">Create your firm workspace</p>
          <h1 className="mt-2 text-2xl font-semibold tracking-tight text-navy-900">
            {plan.name}, {cycle === 'monthly' ? 'billed monthly' : 'billed yearly'}
          </h1>
          <p className="mt-2 text-[13.5px] leading-relaxed text-slate-600">
            Two things happen next: you create one account (name, email, password), then your firm
            workspace opens on a 14-day trial. Nothing is charged until you confirm the invoice for the plan below.
          </p>

          <button
            onClick={start}
            disabled={busy}
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md bg-navy-900 px-5 py-3 text-sm font-semibold text-brand-100 disabled:opacity-60"
          >
            {busy ? 'Opening secure sign-up…' : 'Create my account'}
            {!busy && <ArrowRight className="h-4 w-4" />}
          </button>
          {error && <p className="mt-2 text-[12.5px] text-red-600">{error}</p>}

          <ul className="mt-6 space-y-2.5">
            {plan.includes.map(item => (
              <li key={item} className="flex gap-2 text-[13px] text-slate-600">
                <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-600" />{item}
              </li>
            ))}
          </ul>

          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-slate-100 pt-4 text-[11.5px] text-slate-400">
            <span className="inline-flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" />Microsoft-hosted sign-up</span>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" />Your firm sees only its own data</span>
            <span className="inline-flex items-center gap-1.5"><Users className="h-3.5 w-3.5" />Add the team after you are in</span>
          </div>

          <p className="mt-4 text-[11.5px] text-slate-400">
            By creating an account you agree to the{' '}
            <a href="/terms.html" className="underline hover:text-slate-600">Terms of Use</a> and{' '}
            <a href="/privacy.html" className="underline hover:text-slate-600">Privacy Policy</a>.
          </p>
        </section>

        <aside className="rounded-2xl border border-slate-200 bg-white p-7">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">What you are signing up for</p>
          <h2 className="mt-2 text-lg font-semibold text-navy-900">{plan.name}</h2>
          <p className="mt-1 text-[12.5px] text-slate-500">{plan.line}</p>

          <div className="mt-5 inline-flex rounded-md border border-slate-200 bg-slate-50 p-1">
            {[
              { id: 'annual', label: 'Yearly' },
              { id: 'monthly', label: 'Monthly' },
            ].map(opt => (
              <button
                key={opt.id}
                onClick={() => navigate(`/signup?plan=${planId}&cycle=${opt.id}`)}
                className={`rounded px-3 py-1.5 text-[12.5px] font-semibold ${cycle === opt.id ? 'bg-white text-navy-900 shadow-sm' : 'text-slate-500'}`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <dl className="mt-5 space-y-2.5 text-[13px]">
            <div className="flex items-baseline justify-between">
              <dt className="text-slate-500">Per seat</dt>
              <dd className="font-semibold text-navy-900">{money(rate)} / month</dd>
            </div>
            <div className="flex items-baseline justify-between">
              <dt className="text-slate-500">Seats included</dt>
              <dd className="font-semibold text-navy-900">{seatCount} minimum</dd>
            </div>
            <div className="flex items-baseline justify-between border-t border-slate-100 pt-2.5">
              <dt className="text-slate-500">{cycle === 'monthly' ? 'Monthly total' : 'Annual total'}</dt>
              <dd className="text-lg font-semibold text-navy-900">{money(invoiceTotal)}</dd>
            </div>
            {cycle === 'annual' && (
              <div className="flex items-baseline justify-between">
                <dt className="text-slate-500">Monthly equivalent</dt>
                <dd className="text-slate-700">{money(monthlyEquivalent)} / month</dd>
              </div>
            )}
            {cycle === 'monthly' && (
              <div className="flex items-baseline justify-between">
                <dt className="text-slate-500">Over a year</dt>
                <dd className="text-slate-700">{money(monthlyEquivalent * 12)} (20% more than yearly)</dd>
              </div>
            )}
          </dl>

          <p className="mt-5 rounded-lg bg-slate-50 p-3 text-[11.5px] leading-relaxed text-slate-500">
            Invoiced, not card-charged. Change the plan, add seats or switch cycle from Plan &amp; Billing
            at any time before the invoice is settled.
          </p>
        </aside>
      </main>
    </div>
  );
}
